import {gql} from '@apollo/client'
import {Option} from 'jbc-front/components/presenters/form/Select'
import {CustomEmployeeFieldGroup, Office} from '../create/query'
import {Schema} from '../create/schema'

interface AlertTypeCustomDate {
  customEmployeeFieldId: number
}

interface AlertTypeNotification {
  active: boolean
  destination: 'admin' | 'employee'
  subject: string
  body: string
}

export interface AlertType {
  id: string
  color: number
  name: string
  description: string
  baseDateType: string
  alertTargets: string[]
  offset: number
  alertTypeLeaveOfAbsenceReason: string
  days: number
  dateUnit: string
  active: boolean
  alertTypeCustomDate?: AlertTypeCustomDate
  alertTypeNotifications: [AlertTypeNotification, AlertTypeNotification]
  selectedOffices: [Office]
  selectedAllOffices: boolean
  editable: boolean
}

export interface AlertTypeQueryResult {
  alertType: AlertType
}

export interface CustomEmployeeFieldGroupQuery {
  customEmployeeFieldGroups: CustomEmployeeFieldGroup[]
}

export const ConvertQueryAlertTypeToFormData = (
  alertType: AlertType,
  baseDateTypeOptions: Option[],
  reasonTypeOptions: Option[],
  alertTargetOptions: Option[]
): Schema => {
  const isBefore = alertType.days < 0 ? 'Before' : 'After'
  const days = alertType.days > 0 ? alertType.days : -alertType.days

  const defaultBaseDateTypeOption: Option = {label: '生年月日', value: 'birthday'}
  const baseDateType: Option = alertType?.alertTypeCustomDate?.customEmployeeFieldId
    ? baseDateTypeOptions.find(
        (option) => option.value === alertType?.alertTypeCustomDate?.customEmployeeFieldId.toString()
      ) || defaultBaseDateTypeOption
    : baseDateTypeOptions.find((option) => option.value === alertType.baseDateType) || defaultBaseDateTypeOption

  const defaultAlertTargetOption: Option = {label: '本人', value: 'self'}
  const filteredTargets = alertTargetOptions.filter((option) => alertType?.alertTargets?.includes(option.value))
  const alertTargets: Option[] = filteredTargets.length > 0 ? filteredTargets : [defaultAlertTargetOption]

  const defaultReasonTypeOption: Option = {label: '（すべて）', value: 'all'}
  const reasonType: Option =
    reasonTypeOptions.find((option) => option.value === alertType?.alertTypeLeaveOfAbsenceReason) ||
    defaultReasonTypeOption

  const adminMail = alertType.alertTypeNotifications.find((atn) => atn.destination === 'admin')
  const employeeMail = alertType.alertTypeNotifications.find((atn) => atn.destination === 'employee')

  const selectedOfficeIds = alertType.selectedOffices?.map((office: Office) => office.id) ?? []

  const alertTypeInput: Schema = {
    color: alertType.color,
    name: alertType.name,
    description: alertType.description,
    baseDateType: baseDateType,
    alertTargets: alertTargets,
    offset: String(alertType.offset),
    isBefore: isBefore,
    days: String(days),
    dateUnit: alertType.dateUnit,
    active: alertType.active,
    reasonType: reasonType,
    adminMailActive: adminMail?.active ?? false,
    adminMailSubject: adminMail?.subject ?? '',
    adminMailBody: adminMail?.body ?? '',
    employeeMailActive: employeeMail?.active ?? false,
    employeeMailSubject: employeeMail?.subject ?? '',
    employeeMailBody: employeeMail?.body ?? '',
    selectedOfficeIds: selectedOfficeIds,
    selectedAllOffices: alertType.selectedAllOffices,
  }
  return alertTypeInput
}

export const ALERT_TYPE = gql`
  query alertType($id: ID!) {
    alertType(id: $id) {
      id
      color
      name
      clientId
      description
      baseDateType
      offset
      days
      dateUnit
      active
      alertTypeCustomDate {
        customEmployeeFieldId
      }
      alertTypeNotifications {
        destination
        subject
        body
        active
      }
      alertTypeLeaveOfAbsenceReason
      alertTargets
      selectedOffices {
        id
        name
      }
      selectedAllOffices
      editable
    }
  }
`
